import type { ExtensionContext } from "@oh-my-pi/pi-coding-agent";
import {
  assignRefs,
  createCore,
  createInitialState,
  defaultCountTokens,
  defaultPrompts,
  highestUsedIndex,
  type CompressionCore,
  type CompressionState,
  type Config,
  type CoreMessage,
  type Prompts,
} from "acp-kernel";
import { resolveConfig, resolveFoldPersistence, type AdapterConfig } from "./config.js";
import { createFoldStore, foldPersistDir, restoreFoldSnapshot, type FoldSnapshot } from "./fold-persist.js";
import { debug, logInfo, logWarn } from "./log.js";
import { getSystemPromptText } from "./compat.js";
import { buildAcpSystemPrompt } from "./system-prompt.js";
import {
  coreIdentity,
  findCompressCallsCore,
  staleRangeCore,
  toolCallNames,
  toolResultTextsCore,
  viewToAnthropicCore,
  viewToCoreStream,
  viewToResponsesCore,
} from "./wire-fold.js";
import type { BiliMessage } from "acp-kernel/wire";
import type { AgentMessage } from "./messages.js";

/** Core-space fold result (provider mode): no originalById — the output
 *  rebuilds straight onto the wire via the kernel codecs. */
export interface CoreFoldResult {
  state: CompressionState;
  coreMessages: BiliMessage[];
  streamLen: number;
}

interface FoldSlot {
  identities: string[];
  foldedLen: number;
  preview: boolean;
  /** True once this slot was produced by a LIVE wire fold, a compress
   *  commit, or a checkpoint restore — NOT by a primeFold mirror or by the
   *  read path (stateFor → coreSlotFor) lazily materializing a placeholder.
   *  restoreFold's skip-live guard keys on this: a placeholder slot must
   *  never suppress a restore or the mirror fallback (observed: acp_status
   *  between session_start and session_switch created a 0-block placeholder,
   *  session_switch then skipped restore AND primeFold → "Blocks: none"). */
  live: boolean;
  state: CompressionState;
  coreMessages: CoreMessage[];
  appliedCallIds: Set<string>;
  rejectStreak: number;
}

export interface AcpRuntime {
  core: CompressionCore;
  adapter: AdapterConfig;
  setAdapter(adapter: AdapterConfig): void;
  prompts: Prompts;
  setPrompts(prompts: Prompts): void;
  liveContextLimit(ctx: ExtensionContext): number;
  configFor(ctx: ExtensionContext): Config;
  /** Core-space fold (provider mode): the wire payload already parsed to
 *  BiliMessage[] by the kernel codec; incremental LCP + replay semantics,
 *  content-hash id space (wire-fold.ts). */
  foldStreamCore(ctx: ExtensionContext, stream: BiliMessage[]): CoreFoldResult;
  stateFor(ctx: ExtensionContext): Promise<{ state: CompressionState; coreMessages: CoreMessage[] }>;
  /** Commit the folded state to the core slot — the mirror of stateFor: a
   *  commit must land where the next read goes (issue #90). */
  commitFoldState(ctx: ExtensionContext, state: CompressionState, toolCallId?: string): void;
  /** Track the per-session streak of consecutively REJECTED compress calls.
   *  `ok=false` increments and returns the new streak; `ok=true` resets to 0.
   *  A re-fold (rewritten stream prefix) drops the slot and starts at 0. */
  noteCompressOutcome(ctx: ExtensionContext, ok: boolean): number;
  /** Current consecutive-reject streak in the space the session's CURRENT
   *  mode folds in (issue #104: the nudge must not demand compression while
   *  compress calls are being rejected in a row). */
  rejectStreakFor(ctx: ExtensionContext): number;
  /** Restore the fold slot from the previous process's checkpoint (issue
   *  #130). Returns true when the session already has a live in-memory slot
   *  (in-process switch back — never clobbered) or a valid checkpoint was
   *  loaded. Callers skip the primeFold mirror: a mirror that guesses the
   *  host's view→wire projection lands in a different fingerprint space,
   *  which is exactly the bug the checkpoint exists to fix. */
  restoreFold(ctx: ExtensionContext): boolean;
  /** Debounced checkpoint of the live fold slot. Called from the live fold
   *  path only (wire fold, turn commit, compress commit) — preview/mirror
   *  slots are never persisted. No-op when persistence is disabled. */
  scheduleFoldSnapshot(ctx: ExtensionContext): void;
  /** Synchronously flush the session's checkpoint. For session_shutdown:
   *  the host may exit as soon as the handler returns, so a debounced save
   *  would be dropped with the process. Returns false when the write
   *  failed (the in-memory slot is unaffected either way). */
  flushFoldSync(sid: string): boolean;
  forgetSession(sid: string): void;
  /** Rebuild blocks from the persisted session at session_start so /acp and
   *  acp_status show them BEFORE the first LLM call of a resumed session.
   *  Provider mode folds the WIRE projection (wire-fold.ts mirrors — the
   *  authoritative fold runs on the wire-synthesized stream, which differs
   *  from the raw session view; issue #64). The slot is marked preview and
   *  always re-folded authoritatively at the first live event (the live
   *  stream is the truth source, not the persisted view). */
  primeFold(ctx: ExtensionContext): void;
  acquireLock(sid: string): Promise<() => void>;
}

function freshSlot(preserveFrom?: FoldSlot): FoldSlot {
  const slot: FoldSlot = { identities: [], foldedLen: 0, preview: false, live: false, state: createInitialState(), coreMessages: [], appliedCallIds: new Set(), rejectStreak: 0 };
  // Cadence stamps and the reject streak are SESSION-level accounting ("when
  // was the model last reminded" / "how many compress calls has it had
  // rejected in a row"), not stream-derived state. A re-fold rebuilds blocks
  // and refs deterministically from the stream, but must not forget either:
  // omp fires back-to-back context events on DIFFERENT views of the same
  // session (its recap/subagent pipelines re-feed our own rebuilt output;
  // observed live: context-out msgs=78 at 13:41:11.430 became context-in
  // msgs=78 the same millisecond). Clearing stamps there armed the
  // growth-floor gate from zero and re-fired the nudge 4ms after the previous
  // one; clearing the streak would likewise disarm the issue-47 loop guard
  // mid-loop (each view flip resets it to 0 and the guard never reaches its
  // escalation threshold). Kernel-side epoch resets (a real compression
  // clears the stamps via applyCompression) are unaffected — they act on the
  // state we preserve. A successful compression resets the streak explicitly
  // via noteCompressOutcome.
  if (preserveFrom) {
    slot.state = { ...slot.state, nudge: preserveFrom.state.nudge };
    slot.rejectStreak = preserveFrom.rejectStreak;
  }
  return slot;
}

/** Is this re-fold a VIEW FLIP rather than a real history change? A view
 *  flip keeps the stream's identity prefix — omp's variant pipelines
 *  (recap/subagent/advisor) rebuild the same history with a different
 *  projection granularity, so LCP stays high and only the tail differs. A
 *  host compaction (/compact) rewrites the HEAD: the summary message is
 *  first, LCP collapses to ~0. Live evidence: post-compress variant request
 *  at 02:05:06 had foldedLen=198 lcp=197 streamLen=197 (flip, blocks must
 *  survive); post-/compact at 02:27:55 had foldedLen=269 lcp=1 streamLen=40
 *  (compaction, fresh fold is correct). */
function isViewFlip(foldedLen: number, lcp: number): boolean {
  return foldedLen > 0 && lcp >= Math.floor(foldedLen / 2);
}

/** Fold the same session through a different view. Compression blocks are
 *  SESSION-level state on a view flip: the model compressed against the
 *  OTHER view's projection, whose positional ids (p1..pN) and boundary
 *  fingerprints do not exist in this view's projection — replaying the call
 *  here systematically fails the fingerprint guard (observed live:
 *  "reason=fp m00001..m00100 want 24fba6b4 got 0fba5795 @p1..p88" 11ms
 *  after `compress applied 5 blocks`) and freshSlot's createInitialState
 *  threw the just-created blocks away — 3 consecutive live compressions
 *  each evaporated within one request, the session re-nudged and re-paid
 *  them every time. Carrying blocks/refs/appliedCallIds keeps the live
 *  compression; the wrapped calls stay marked applied so this fold does not
 *  try to re-run them against this view's projection. */
function preserveCompressedSlot(prev: FoldSlot): FoldSlot {
  const slot = freshSlot(prev);
  slot.state = { ...slot.state, blocks: prev.state.blocks, messageRefs: prev.state.messageRefs, stats: prev.state.stats, nextBlockId: prev.state.nextBlockId, nextRunId: prev.state.nextRunId };
  slot.appliedCallIds = new Set(prev.appliedCallIds);
  return slot;
}

function stateHasCompressCall(state: CompressionState, callId: string): boolean {
  return state.blocks.some((b) => b.compressCallId === callId);
}

export function createRuntime(adapter: AdapterConfig): AcpRuntime {
  const core = createCore({ countTokens: defaultCountTokens });
  const locks = new Map<string, Promise<void>>();
  // Core-space slot space for the provider fold — content-hash ids
  // (wire-fold.ts). The legacy context-space slot (pN ids) was removed.
  const coreSlots = new Map<string, FoldSlot>();
  let adapterRef = adapter;
  let promptsRef: Prompts = defaultPrompts;
  // Fold-slot checkpoint store (issue #130): mechanism in acp-kernel/persist,
  // policy (dir, enable, schema version) in fold-persist.ts. Built once per
  // runtime; setAdapter does not rebind it — persistence follows the plugin
  // process, not per-session adapter config.
  const foldPersist = resolveFoldPersistence(adapter);
  const foldStore = createFoldStore(foldPersistDir(foldPersist.dir), foldPersist.enabled);

  async function acquireLock(sid: string): Promise<() => void> {
    const prev = locks.get(sid) ?? Promise.resolve();
    let release!: () => void;
    const next = new Promise<void>((resolve) => { release = resolve; });
    locks.set(sid, next);
    await prev;
    return release;
  }

  function liveContextLimit(ctx: ExtensionContext): number {
    const usage = ctx.getContextUsage?.();
    if (usage?.contextWindow && usage.contextWindow > 0) return usage.contextWindow;
    const m = ctx.model as { contextWindow?: number } | undefined;
    return m?.contextWindow ?? 0;
  }

  function configFor(ctx: ExtensionContext): Config {
    return resolveConfig(adapterRef, liveContextLimit(ctx));
  }

  function coreSlotFor(sid: string): FoldSlot {
    let slot = coreSlots.get(sid);
    if (!slot) {
      slot = freshSlot();
      coreSlots.set(sid, slot);
    }
    return slot;
  }

  function sidOf(ctx: ExtensionContext): string {
    return ctx.sessionManager.getSessionId();
  }

  // The input stream (event.messages) is the single source of truth. In the
  // wire space identity is content-hash based (coreIdentity, wire-fold.ts):
  // the same wire recomputes the same ids — which is exactly what makes a
  // cross-process checkpoint meaningful. Compression blocks are DERIVED
  // state: each compress tool call lives in the stream itself, so the block
  // ledger is rebuilt by replaying the calls — no host ids, no cross-view
  // alignment. Retry/rewind/compaction shrink or rewrite the stream; the
  // fold detects a mutated prefix and re-folds from scratch, so state always
  // equals fold(stream).
  // EXCEPTION (issue #130): at restart the wire payload is not available
  // until the first provider request, so primeFold mirrors the fold from the
  // session VIEW — but the view→wire projection is host-owned (pi-ai
  // transformMessages + convertMessages: cross-model thinking demotion,
  // developer→user remap, empty-message drops) and the mirror lands in a
  // different fingerprint space; every replay then failed the span guard
  // (observed live: 1988 mirror pieces vs 1692 live, 0/14 anchors). The
  // live fold slot is therefore checkpointed to disk (fold-persist.ts) and
  // restored at session_start before any mirror runs. The first live fold
  // still re-validates the restored slot via LCP; a stale checkpoint
  // degrades to the normal re-fold, never worse.
  function foldStreamCore(ctx: ExtensionContext, stream: BiliMessage[]): CoreFoldResult {
    const sid = sidOf(ctx);
    let slot = coreSlotFor(sid);
    if (slot.preview) {
      debug.event("fold-refold", { sid, foldedLen: slot.foldedLen, lcp: 0, streamLen: stream.length, reason: "preview" });
      slot = freshSlot(slot);
      coreSlots.set(sid, slot);
    }
    const ids = stream.map(coreIdentity);
    let lcp = 0;
    while (lcp < Math.min(ids.length, slot.identities.length) && ids[lcp] === slot.identities[lcp]) lcp++;
    if (lcp < slot.foldedLen) {
      const flip = isViewFlip(slot.foldedLen, lcp);
      debug.event("fold-refold", { sid, foldedLen: slot.foldedLen, lcp, streamLen: stream.length, flip, space: "core" });
      slot = flip ? preserveCompressedSlot(slot) : freshSlot(slot);
      coreSlots.set(sid, slot);
      lcp = 0;
    }
    // Restored-block revalidation (issue #130 follow-up): blocks carried from
    // a checkpoint must still reference THIS stream. A restored slot whose
    // LCP collapsed to 0 keeps stale blocks whose blockIds the next
    // allocateBlockId WILL reuse (nextBlockId resets on the fresh fold), so
    // syncBlocks' consumed-set later deactivates the NEW blocks by collision
    // — observed live: 54 blocks / 5 active, sent tokens never dropped after
    // compressing 351k. Purge blocks whose effective ids no longer resolve
    // in this stream before any replay can run.
    if (lcp === 0 && slot.state.blocks.length > 0) {
      // Compare in the SAME id space the blocks were minted in: block
      // effectiveMessageIds are h_ content-hash ids (deriveMessageId), not
      // the coreIdentity JSON strings used for LCP. Comparing across spaces
      // made every refold purge ALL carried blocks — a view flip (which
      // resets lcp to 0 after preserveCompressedSlot) lost the preserved
      // blocks immediately, and a fresh compression then had no blocks to
      // build directBlockIds on.
      const presentIds = new Set(stream.map((m) => m.id));
      const surviving = slot.state.blocks.filter((b) => b.active === false || b.effectiveMessageIds.some((id) => presentIds.has(id)));
      if (surviving.length < slot.state.blocks.length) {
        debug.event("fold-purge-stale-blocks", { sid, before: slot.state.blocks.length, after: surviving.length });
        slot.state = { ...slot.state, blocks: surviving };
      }
    }

    const coreMessages = stream;
    const config = configFor(ctx);

    // The kernel codecs do not carry tool names on tool-result pieces —
    // protection checks resolve the name through the call pieces.
    const names = toolCallNames(stream);
    const assigned = assignRefs(coreMessages, {
      existing: slot.state.messageRefs,
      nextIndex: highestUsedIndex(slot.state.messageRefs) + 1,
      isProtected: (m) => {
        if (m.role !== "tool" || !m.toolCallId) return false;
        const name = names.get(m.toolCallId);
        if (!name) return false;
        if (name === "compress") return true;
        return (config.protectedTools ?? []).includes(name);
      },
    });
    slot.state = { ...slot.state, messageRefs: assigned.map };

    const isFreshFold = slot.foldedLen === 0;
    const resultTexts = toolResultTextsCore(stream);
    let replayed = 0;
    for (let i = isFreshFold ? 0 : slot.foldedLen; i < stream.length; i++) {
      for (const call of findCompressCallsCore(stream[i]!)) {
        const resultText = resultTexts.get(call.id) ?? "";
        if (resultText.includes("No changes applied")) {
          debug.event("fold-replay-skipped", { sid, callId: call.id });
          continue;
        }
        if (slot.appliedCallIds.has(call.id) || stateHasCompressCall(slot.state, call.id)) continue;
        // Guard verdicts: reject → drop (master semantics); a position-
        // recovered boundary comes back remapped to the CURRENT ref of the
        // piece it recovered — the kernel resolves ranges by ref, and a
        // drifted fold leaves the recorded m-ref dangling (issue #91).
        const verdicts = call.ranges.map((r, ri) => staleRangeCore(r, ri, resultText, coreMessages, i, slot.state.messageRefs.byRef, slot.state.blocks));
        const stale = verdicts.find((v) => v.reject);
        if (stale) {
          debug.event("fold-replay-stale", { sid, callId: call.id, reason: stale.reject });
          // A recovery that FAILED (pos hint present, range still dropped) is
          // exactly the issue-#91 symptom resurfacing — always-on, not just
          // ACP_DEBUG (a plain stale drop without hint stays debug-only, as
          // on master).
          const failed = verdicts.find((v) => v.hint && v.reject);
          if (failed) logWarn("fold", { sid, event: "replay-recovery-failed", callId: call.id, reason: failed!.reject });
          continue;
        }
        const recovered = verdicts.find((v) => v.recovered);
        const ranges = recovered
          ? call.ranges.map((r, ri) => {
              const m = verdicts[ri]!.remap;
              return m ? { ...r, startRef: m.startRef ?? r.startRef, endRef: m.endRef ?? r.endRef } : r;
            })
          : call.ranges;
        if (recovered) {
          logWarn("fold", { sid, event: "replay-recovered", callId: call.id, pos: recovered.recovered!.pos, startIdx: recovered.recovered!.startIdx, endIdx: recovered.recovered!.endIdx });
        }
        try {
          const applied = core.applyCompression({ ranges, messages: coreMessages, state: slot.state, config });
          if (applied.result.errors.length === 0) {
            slot.state = applied.state;
            replayed++;
            debug.event("fold-replay", { sid, callId: call.id, ranges: call.ranges.length });
          } else {
            logWarn("fold", { sid, event: "replay-rejected", callId: call.id, errors: applied.result.errors.slice(0, 3) });
          }
        } catch (e) {
          logWarn("fold", { sid, event: "replay-failed", callId: call.id, error: e instanceof Error ? e.message : String(e) });
        }
        slot.appliedCallIds.add(call.id);
      }
    }
    if (replayed > 0) logWarn("fold", { sid, event: "replayed", calls: replayed });

    slot.identities = ids;
    slot.foldedLen = ids.length;
    slot.coreMessages = coreMessages;
    if (!slot.preview) {
      slot.live = true;
      scheduleFoldSnapshotById(sid);
    }

    return { state: slot.state, coreMessages, streamLen: stream.length };
  }

  /** Read the fold slot. Safe WITHOUT the lock only because this returns
   *  `Promise.resolve` over the live slot — fully synchronous, no await
   *  between read and use, so no concurrent fold can interleave. The
   *  read-only tools (decompress/search/acp_status) rely on this invariant;
   *  if this ever becomes genuinely async, take `acquireLock` in every
   *  caller or here (issue #32). */
  function stateFor(ctx: ExtensionContext): Promise<{ state: CompressionState; coreMessages: CoreMessage[] }> {
    const sid = sidOf(ctx);
    // The two slot spaces never mix within a session; surface the space the
    // session's CURRENT mode folds in (a mid-session mode flip means the old
    // space's orphaned blocks are deactivated on the next fold, not mixed).
    const slot = slotForMode(ctx, sid);
    return Promise.resolve({ state: slot.state, coreMessages: slot.coreMessages });
  }

  function primeFold(ctx: ExtensionContext): void {
    const sid = sidOf(ctx);
    try {
      // ReadonlySessionManager's type omits buildSessionContext, but the
      // runtime object has it — it builds exactly the view omp feeds the
      // agent on resume. Purely a preview: discarded at the first live
      // event, so a mismatching projection can never leak into live state.
      const sm = ctx.sessionManager as unknown as {
        buildSessionContext?: () => { messages?: AgentMessage[] };
      };
      const view = sm.buildSessionContext?.().messages ?? [];
      if (view.length === 0) return;
      // The authoritative fold runs on the WIRE projection — a different
      // space than this view. The raw view is NOT equivalent: for openai-
      // style payloads the system prompt is a message in the wire body (it
      // takes m00001) and tool entries carry no names (a compress result
      // would not be ref-BLOCKED); folding the raw view puts the fold in a
      // different ref/fingerprint space: stored span fingerprints mismatch,
      // the guard rejects every in-stream replay, and a resumed session
      // shows "Blocks: none" until the first provider request (issue #64).
      // Fold the format-matched wire mirror instead — openai-shape (system
      // first) for openai/ollama payloads, anthropic-shape for anthropic-
      // messages (system stays top-level, out of the fold space), responses-
      // shape for openai-responses (system in `instructions`, out of the
      // fold space). The system text mirrors what before_agent_start puts
      // on the wire (base + ACP block — not yet appended at session_start).
      const api = (ctx.model as { api?: string } | undefined)?.api ?? "";
      let stream: BiliMessage[];
      if (api === "anthropic-messages") {
        stream = viewToAnthropicCore(view);
      } else if (api === "openai-responses") {
        // Responses wire: system prompt lives in the top-level `instructions`
        // field (out of the fold space), conversation in the `input` array.
        // Mirror that layout, or the preview lands in a different ref space
        // than the live request (issue #64, responses variant).
        const base = getSystemPromptText(ctx);
        const acp = buildAcpSystemPrompt(promptsRef);
        stream = viewToResponsesCore(view, base.includes(acp) ? base : `${base}\n\n${acp}`);
      } else {
        const base = getSystemPromptText(ctx);
        const acp = buildAcpSystemPrompt(promptsRef);
        stream = viewToCoreStream(view, base.includes(acp) ? base : `${base}\n\n${acp}`);
      }
      // Thinking serialized as `reasoning_content` field OR demoted-inline
      // `<think>…</think>` is one identity space since acp-kernel 0.0.34
      // (openaiToCore splits the inline form), so one mirror suffices.
      const r = foldStreamCore(ctx, stream);
      coreSlotFor(sid).preview = true;
      logInfo("fold", { sid, event: "prime-fold", msgs: stream.length, wire: true, blocks: r.state.blocks.length });
    } catch (e) {
      logWarn("fold", { sid, event: "prime-fold-failed", error: e instanceof Error ? e.message : String(e) });
    }
  }

  function forgetSession(sid: string): void {
    coreSlots.delete(sid);
    locks.delete(sid);
  }

  function slotForMode(_ctx: ExtensionContext, sid: string): FoldSlot {
    // Provider mode only: the fold always runs in core space on the wire
    // projection. The legacy context-mode slot (slotFor) was removed.
    return coreSlotFor(sid);
  }

  function commitFoldState(ctx: ExtensionContext, state: CompressionState, toolCallId?: string): void {
    const slot = slotForMode(ctx, sidOf(ctx));
    slot.state = state;
    // A compress commit is live truth even if it lands before any wire fold
    // this process (read-path placeholder must not suppress restore).
    slot.live = true;
    if (toolCallId) slot.appliedCallIds.add(toolCallId);
  }

  function noteCompressOutcome(ctx: ExtensionContext, ok: boolean): number {
    const slot = slotForMode(ctx, sidOf(ctx));
    slot.rejectStreak = ok ? 0 : slot.rejectStreak + 1;
    return slot.rejectStreak;
  }
  /** Restore the fold slot from the previous process's checkpoint (issue
   *  #130). An existing in-memory slot (session switch back to a live
   *  session) is live truth and never clobbered. Returns false when nothing
   *  was restored so the caller can fall back to the primeFold mirror.
   *  A checkpoint with zero blocks is NOT a restore: it carries nothing the
   *  fresh slot lacks (freshSlot is 0 blocks too, and LCP over empty
   *  identities is lcp=0 either way), but claiming it WOULD suppress the
   *  mirror — which can still replay compress calls from the transcript.
   *  Observed live in tests: an early debounce flush (500ms) writes a
   *  pre-compress 0-block snapshot; restoring it made session_switch's
   *  skip-live path pin the 0-block slot and the panel showed "0" (the
   *  exact issue #130 symptom from the wrong direction). */
  function restoreFold(ctx: ExtensionContext): boolean {
    const sid = sidOf(ctx);
    const existing = coreSlots.get(sid);
    if (existing?.live) {
      logInfo("fold", { sid, event: "fold-restore-skip-live", blocks: existing.state.blocks.length });
      return true;
    }
    if (!foldPersist.enabled) return false;
    const envelope = foldStore.loadSync(sid);
    if (!envelope) return false;
    const r = restoreFoldSnapshot(envelope.payload);
    if (r.state.blocks.length === 0) {
      logInfo("fold", { sid, event: "fold-restore-empty" });
      return false;
    }
    // Generation hygiene (live incident 8/23: 54 blocks / 5 active, three
    // post-restart compressions totalling ~570k tokens deactivated). Checkpoints
    // written across MANY restarts accumulate block copies from different
    // processes whose `nextBlockId` counters disagree, so fresh
    // allocateBlockId reuses ids (b1 x7, b2 x4 observed). syncBlocks keys the
    // consumed-set by blockId STRING, so an old block's directBlockIds
    // ("b12" from ITS generation) deactivates the new b12 of a LATER
    // generation — the compression lands, then silently dies.
    // 1) Keep only the newest generation per blockId (largest createdAt; the
    //    live block is always the latest allocation).
    // 2) Raise nextBlockId above every surviving id so no future allocation
    //    can collide with any block already in the ledger.
    const byId = new Map<string, typeof r.state.blocks[number]>();
    for (const b of r.state.blocks) {
      const prev = byId.get(b.blockId);
      if (!prev || b.createdAt > prev.createdAt) byId.set(b.blockId, b);
    }
    const blocks = [...byId.values()];
    if (blocks.length < r.state.blocks.length) {
      logInfo("fold", { sid, event: "fold-restore-dedup", before: r.state.blocks.length, after: blocks.length });
    }
    const maxId = blocks.reduce((m, b) => {
      const n = /^b(\d+)$/i.exec(b.blockId);
      return n ? Math.max(m, Number.parseInt(n[1]!, 10)) : m;
    }, 0);
    const state = { ...r.state, blocks, nextBlockId: Math.max(r.state.nextBlockId ?? 1, maxId + 1) };
    coreSlots.set(sid, {
      identities: r.identities,
      foldedLen: r.foldedLen,
      preview: false,
      live: true,
      state,
      coreMessages: r.coreMessages,
      appliedCallIds: r.appliedCallIds,
      rejectStreak: r.rejectStreak,
    });
    logInfo("fold", { sid, event: "fold-restore", blocks: state.blocks.length, foldedLen: r.foldedLen, nextBlockId: state.nextBlockId });
    return true;
  }

  function snapshotOf(slot: FoldSlot): FoldSnapshot {
    return {
      identities: slot.identities,
      foldedLen: slot.foldedLen,
      state: slot.state,
      coreMessages: slot.coreMessages,
      appliedCallIds: [...slot.appliedCallIds],
      rejectStreak: slot.rejectStreak,
    };
  }

  function scheduleFoldSnapshotById(sid: string): void {
    const slot = coreSlots.get(sid);
    if (!slot || slot.state.blocks.length === 0) return;
    foldStore.scheduleSave(sid, () => snapshotOf(slot));
  }

  function scheduleFoldSnapshot(ctx: ExtensionContext): void {
    scheduleFoldSnapshotById(sidOf(ctx));
  }

  function flushFoldSync(sid: string): boolean {
    const slot = coreSlots.get(sid);
    if (!slot) return false;
    return foldStore.flushSync(sid, () => snapshotOf(slot));
  }

  function rejectStreakFor(ctx: ExtensionContext): number {
    return slotForMode(ctx, sidOf(ctx)).rejectStreak;
  }

  return {
    core,
    get adapter() { return adapterRef; },
    setAdapter: (a) => { adapterRef = a; },
    get prompts() { return promptsRef; },
    setPrompts: (p) => { promptsRef = p; },
    liveContextLimit,
    configFor,
    foldStreamCore,
    stateFor,
    commitFoldState,
    noteCompressOutcome,
    restoreFold,
    scheduleFoldSnapshot,
    flushFoldSync,
    rejectStreakFor,
    forgetSession,
    primeFold,
    acquireLock,
  };
}
